import React, { useMemo, useState } from "react";
import Button from "../common/Button/Button";
import DataTableComponent from "../common/DataTableComponent";
import { getTableHeader } from "../systemadministration/CampusManagement/TableComponents";
import {
    assignTableCustomStyles,
    tableHeaderStyle,
    AssignModal,
    AssignModalBody,
    AssignModalHeader,
} from "./careerLibrary.styles";
import { applicableFilterOptions } from "./careerLibraryUtils";

const defaultTableState = {
    page: 1,
    perPage: 10,
    sortKey: "",
    sortOrder: "asc",
};
const emptyFilterState = { arr: [], checkObj: {} };

/**
 * Link dialog shared by careers and PMC records. Each row exposes one explicit
 * link or unlink action; there is intentionally no bulk-selection state.
 */
function LinkPicker({
    label,
    title,
    rows,
    linkedKeys,
    rowKey,
    columns,
    searchText,
    filters = [],
    exportFileName,
    exportRow,
    onToggle,
    open,
    onOpenChange,
}) {
    const [tableState, setTableState] = useState(defaultTableState);
    const [search, setSearch] = useState("");
    const [filterValues, setFilterValues] = useState({});
    const [busyKey, setBusyKey] = useState(null);

    const openPicker = () => {
        setTableState(defaultTableState);
        setSearch("");
        setFilterValues({});
        onOpenChange(true);
    };

    const filteredRows = useMemo(() => {
        const searchTerm = String(search || "")
            .trim()
            .toLowerCase();
        return rows.filter((row) => {
            if (
                searchTerm &&
                !searchText(row).toLowerCase().includes(searchTerm)
            ) {
                return false;
            }

            return filters.every((filter) => {
                const selected = (filterValues[filter.key] || emptyFilterState)
                    .arr;
                return (
                    !selected.length || selected.includes(filter.readValue(row))
                );
            });
        });
    }, [rows, search, searchText, filters, filterValues]);

    const pageStart = (tableState.page - 1) * tableState.perPage;
    const pageRows = filteredRows
        .slice(pageStart, pageStart + tableState.perPage)
        .map((row) => ({
            ...row,
            __pickerKey: rowKey(row),
        }));

    const toggleRow = async (key, isLinked) => {
        setBusyKey(key);
        try {
            await Promise.resolve(onToggle(key, isLinked));
        } finally {
            setBusyKey(null);
        }
    };

    const pickerColumns = [
        ...columns.map((column) => ({
            ...column,
            name: getTableHeader(
                column.name,
                tableState.sortKey === column.selector,
                tableHeaderStyle,
            ),
        })),
        {
            name: "Action",
            width: "52px",
            right: true,
            cell: (row) => {
                const isLinked = linkedKeys.includes(row.__pickerKey);
                return (
                    <Button
                        variant={isLinked ? "danger" : "primary"}
                        iconOnly
                        iconLeft={
                            <i
                                className={
                                    isLinked ? "fal fa-unlink" : "fal fa-plus"
                                }
                            />
                        }
                        onClick={() => toggleRow(row.__pickerKey, isLinked)}
                        title={isLinked ? "Unlink" : "Link"}
                        className="campus-action-button"
                        isLoading={busyKey === row.__pickerKey}
                    />
                );
            },
        },
    ];

    return (
        <>
            <Button
                variant="primary"
                iconLeft={<i className="fal fa-link" />}
                onClick={openPicker}
                title={label}
            >
                {label}
            </Button>

            <AssignModal
                show={open}
                onHide={() => onOpenChange(false)}
                backdropClassName="career-link-picker-backdrop"
                centered
            >
                <AssignModalHeader>
                    <h5 className="modal-title">{title}</h5>
                    <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={() => onOpenChange(false)}
                    >
                        <i className="fal fa-times" aria-hidden="true" />
                    </button>
                </AssignModalHeader>
                <AssignModalBody className="career-link-picker">
                    <DataTableComponent
                        data={pageRows}
                        columns={pickerColumns}
                        state={tableState}
                        setState={setTableState}
                        setSearch={setSearch}
                        totalRows={filteredRows.length}
                        exportFileName={exportFileName}
                        exportFunction={(fileType) =>
                            exportRow && exportRow(fileType, filteredRows)
                        }
                        filterBtnSpacing="10px"
                        filters={filters.map((filter) => ({
                            filterName: filter.label,
                            uniqueId: `picker-${filter.key}`,
                            optionArr: applicableFilterOptions(
                                rows,
                                filters,
                                filterValues,
                                filter,
                            ),
                            renderLabelFunction: filter.renderLabelFunction,
                            state: filterValues[filter.key] || emptyFilterState,
                            setState: (next) => {
                                const current =
                                    filterValues[filter.key] ||
                                    emptyFilterState;
                                const resolved =
                                    typeof next === "function"
                                        ? next(current)
                                        : next;
                                setFilterValues((currentValues) => ({
                                    ...currentValues,
                                    [filter.key]: resolved || emptyFilterState,
                                }));
                                setTableState((currentState) => ({
                                    ...currentState,
                                    page: 1,
                                }));
                            },
                            isOptionReversed: false,
                        }))}
                        keyField="__pickerKey"
                        containerStyle={{
                            padding: 0,
                            border: "none",
                            boxShadow: "none",
                        }}
                        otherTableProps={{
                            customStyles: assignTableCustomStyles,
                        }}
                    />
                </AssignModalBody>
            </AssignModal>
        </>
    );
}

export default LinkPicker;
